import { FeatureFlagKey } from "encamp-shared/src/featureFlags";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { useProcessedManifest } from "hooks/useProcessedManifest";
import { useCallback, useEffect } from "react";
import { useSearchParams } from "react-router-dom";

import { DiscrepancyReviewDialog } from "./DiscrepancyReviewDialog";
import { ManifestReviewDialog } from "./ManifestReviewDialog";
import { parseReviewParam, REVIEW_PARAM_KEY } from "./reviewParam";

interface ReviewPermalinkProps {
  /** The manifest the `?review=` permalink resolves against (the route's manifest). */
  processedManifestId: string;
}

/**
 * URL-driven open surface for the document-review and discrepancies dialogs.
 * Reads `?review=`:
 *  - `review=document` — opens ManifestReviewDialog for the manifest's source document.
 *  - `review=discrepancies` — opens DiscrepancyReviewDialog for the manifest.
 * Closing clears the param. Mount once on the manifest route, alongside
 * CompareSourcesPermalink.
 */
export function ReviewPermalink({ processedManifestId }: ReviewPermalinkProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { isStaff } = useCurrentUser();
  const { featureFlags } = useFeatureFlags();
  const parsed = parseReviewParam(searchParams.get(REVIEW_PARAM_KEY));

  const handleClose = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete(REVIEW_PARAM_KEY);
        return next;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  if (!parsed) return null;

  // Staff-only, matching the in-drawer entry points to these dialogs
  // (ManifestDetailSection: discrepancies behind WasteManifestDiscrepancies,
  // source-document review behind ManifestDocumentIngest) and the sibling
  // CompareSourcesPermalink — keeps the `?review=` deep-link inert for non-staff
  // and flag-off tenants rather than exposing the staff review mutations by URL.
  if (parsed === "discrepancies") {
    if (!isStaff || !featureFlags[FeatureFlagKey.WasteManifestDiscrepancies]) return null;
    return (
      <DiscrepancyReviewDialog processedManifestIds={[processedManifestId]} onClose={handleClose} />
    );
  }

  if (!isStaff || !featureFlags[FeatureFlagKey.ManifestDocumentIngest]) return null;

  return (
    <DocumentReviewPermalink processedManifestId={processedManifestId} onClose={handleClose} />
  );
}

/**
 * Resolves the manifest's source document and opens ManifestReviewDialog. If the
 * manifest has finished loading with no source document there is nothing to
 * review, so it clears the param (defensive — the queue only emits
 * `review=document` when the manifest has a document review).
 */
function DocumentReviewPermalink({
  processedManifestId,
  onClose,
}: {
  processedManifestId: string;
  onClose: () => void;
}) {
  const { processedManifest, loading } = useProcessedManifest(processedManifestId);
  const sourceDocumentId = processedManifest?.sourceDocument?.id;

  useEffect(() => {
    if (!loading && processedManifest && !sourceDocumentId) onClose();
  }, [loading, processedManifest, sourceDocumentId, onClose]);

  if (!sourceDocumentId) return null;

  return (
    <ManifestReviewDialog
      onClose={onClose}
      documentIds={[sourceDocumentId]}
      wasteManifestId={processedManifest?.documentManifestId ?? undefined}
    />
  );
}
